import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Eye, EyeOff, Lock, Shield } from 'lucide-react';
import Onboarding from './Onboarding';

const ChangePassword = ({ onPasswordChanged, isForced = false }) => {
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { changePassword, user } = useAuth();

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Le mot de passe doit contenir au moins 8 caracteres';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Le mot de passe doit contenir au moins une minuscule';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Le mot de passe doit contenir au moins une majuscule';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Le mot de passe doit contenir au moins un chiffre';
    }
    if (!/(?=.*[@$!%*?&])/.test(password)) {
      return 'Le mot de passe doit contenir au moins un caractere special (@$!%*?&)';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const passwordError = validatePassword(passwords.newPassword);
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }

    if (passwords.newPassword !== passwords.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      setLoading(false);
      return;
    }

    const result = await changePassword(passwords.currentPassword, passwords.newPassword);
    
    if (result.success) {
      setSuccess('Mot de passe change avec succes !');
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      
      // Set flag to show onboarding after password change
      const userId = result.user?.id || user?.id;
      
      if (isForced && userId) {
        const onboardingKey = `show_onboarding_${userId}`;
        localStorage.setItem(onboardingKey, 'true');
      }
      
      // Wait to show success message, then let React handle the navigation
      setTimeout(() => {
        onPasswordChanged();
      }, 1000);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleOnboardingComplete = () => {
    // Store completion per user
    const userId = user?.id || 'unknown';
    const onboardingKey = `onboarding_completed_${userId}`;
    localStorage.setItem(onboardingKey, 'true');
    
    setShowOnboarding(false);
    onPasswordChanged();
  };

  const handleOnboardingSkip = () => {
    // Store completion per user
    const userId = user?.id || 'unknown';
    const onboardingKey = `onboarding_completed_${userId}`;
    localStorage.setItem(onboardingKey, 'true');
    
    setShowOnboarding(false);
    onPasswordChanged();
  };

  const toggleShowPassword = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  if (showOnboarding) {
    return (
      <Onboarding 
        onComplete={handleOnboardingComplete}
        onSkip={handleOnboardingSkip}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-100">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
            <Shield className="h-6 w-6 text-amber-600" />
          </div>
          <CardTitle className="text-2xl font-bold">
            {isForced ? 'Changement obligatoire' : 'Changer le mot de passe'}
          </CardTitle>
          <CardDescription>
            {isForced 
              ? 'Vous devez changer votre mot de passe pour continuer'
              : 'Modifiez votre mot de passe de connexion'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert className="mb-6 border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}
          
          {success && (
            <Alert className="mb-6 border-green-200 bg-green-50">
              <AlertDescription className="text-green-800">
                {success}
              </AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Mot de passe actuel</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  id="currentPassword"
                  type={showPasswords.current ? 'text' : 'password'}
                  placeholder="Mot de passe actuel"
                  value={passwords.currentPassword}
                  onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
                  className="pl-10 pr-10"
                  required
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => toggleShowPassword('current')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="newPassword">Nouveau mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  id="newPassword"
                  type={showPasswords.new ? 'text' : 'password'}
                  placeholder="Nouveau mot de passe"
                  value={passwords.newPassword}
                  onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                  className="pl-10 pr-10"
                  required
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => toggleShowPassword('new')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  id="confirmPassword"
                  type={showPasswords.confirm ? 'text' : 'password'}
                  placeholder="Confirmez le nouveau mot de passe"
                  value={passwords.confirmPassword}
                  onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                  className="pl-10 pr-10"
                  required
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => toggleShowPassword('confirm')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div className="text-xs text-gray-600 bg-gray-50 p-3 rounded">
              <p className="font-medium mb-1">Le mot de passe doit contenir :</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Au moins 8 caracteres</li>
                <li>Une majuscule et une minuscule</li>
                <li>Au moins un chiffre</li>
                <li>Un caractere special (@$!%*?&)</li>
              </ul>
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
              data-testid="change-password-button"
            >
              {loading ? 'Changement...' : 'Changer le mot de passe'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChangePassword;